import { z } from "zod";
import { TApiObjectBase } from "../../../base";
import {
  TApiCollectionCustomField,
  TApiCustomField,
} from "../../../customFields/customFields";
import { TRawCollectionItemFieldSchema } from "../../../property/collection/propertyCollection";
import { TApiFile, IApiFile } from "../../../file/apiFile";
import { TReadAccess } from "../../../common/readAccess";

// Generic collection item
export const TApiCollectionItem = z
  .object({
    name: z.string(),
    fields: z.record(TRawCollectionItemFieldSchema).optional(),
    readAccess: TReadAccess.optional(),
    folderID: z.string().nullish(),
    propertyFileIDs: z.array(z.string()).optional(),
    fileIDs: z.array(z.string()).optional(),
    childType: z.literal("COLLECTION_ITEM"),
  })
  .extend(TApiObjectBase.shape);
export type IApiCollectionItem = z.infer<typeof TApiCollectionItem>;

// Collection folder children types
export type IApiCollectionFolderChild = {
  id: string;
  name: string;
  parentFolderId?: string | null;
  children?: IApiCollectionItem[];
  subFolders?: IApiCollectionFolderChild[];
};
export const TApiCollectionFolderChild: z.ZodType<IApiCollectionFolderChild> =
  z.object({
    id: z.string(),
    name: z.string(),
    parentFolderId: z.string().nullish(),
    children: z.array(z.lazy(() => TApiCollectionItem)),
    subFolders: z.array(z.lazy(() => TApiCollectionFolderChild)),
  });

// Collection item children types
export type IApiCollectionItemFolderChild = {
  id: string;
  name: string;
  parentFolderId?: string | null;
  children?: IApiFile[];
  subFolders?: IApiCollectionItemFolderChild[];
};
export const TApiCollectionItemFolderChild: z.ZodType<IApiCollectionItemFolderChild> =
  z.object({
    id: z.string(),
    name: z.string(),
    parentFolderId: z.string().nullish(),
    children: z.array(z.lazy(() => TApiFile)),
    subFolders: z.array(z.lazy(() => TApiCollectionItemFolderChild)),
  });

// API Responses
export type IApiGetFilesResponse = z.infer<typeof TApiGetFilesResponse>;
export const TApiGetFilesResponse = z.array(TApiFile);

export type IApiGetCollectionItemsResponse = z.infer<
  typeof TApiGetItemsResponse
>;
export const TApiGetItemsResponse = z.object({
  items: z.array(TApiCollectionItem),
  customFields: z.array(TApiCustomField),
});

export type IApiGetCollectionFoldersResponse = z.infer<
  typeof TApiGetCollectionFoldersResponse
>;
export const TApiGetCollectionFoldersResponse = z.object({
  folders: z.array(TApiCollectionFolderChild),
  customFields: z.array(TApiCollectionCustomField),
});

export type IApiGetCollectionItemFoldersResponse = z.infer<
  typeof TApiGetCollectionItemFoldersResponse
>;
export const TApiGetCollectionItemFoldersResponse = z.object({
  folders: z.array(TApiCollectionItemFolderChild),
});
